import React, { useEffect, useRef, useState } from "react";
import CinematicDuck from "./DuckSvg";

/**
 * SLOW-DOWN Easter Egg.
 *
 * When the user clicks (or taps) *anywhere* on the screen 11 times in under
 * 2.5 seconds, we interrupt them with a full-screen Pixar-style stinger:
 * duck bounces in with squash & stretch, big red STOP sign spins in from
 * above, screen shakes, a speech bubble pops, and a comedic quack-honk
 * plays. Auto-dismisses after ~3.6s.
 *
 * Only counts real user input (pointerdown in capture phase). Ignores
 * click events fired programmatically without a genuine pointer.
 */
const CLICKS_REQUIRED = 11;
const TIME_WINDOW_MS = 2500;
const OVERLAY_MS = 3600;
const COOLDOWN_MS = 6000;

const FUNNY_LINES = [
  "You click faster than a duck chases bread.",
  "Whoa there, speedster — my flippers can't keep up.",
  "Easy on the trigger finger, partner.",
  "This isn't Whack-A-Mole. Pace yourself.",
  "Your mouse called. It wants a break.",
];

export default function SlowDownEgg() {
  const [active, setActive] = useState(false);
  const [line, setLine] = useState(FUNNY_LINES[0]);
  const clicksRef = useRef([]);
  const cooldownUntilRef = useRef(0);
  const activeRef = useRef(false);

  useEffect(() => {
    const onPointerDown = (e) => {
      // Ignore synthetic events without a real pointer
      if (e.pointerType === undefined) return;
      const now = Date.now();
      // Cooldown: don't retrigger for a beat after the last stinger
      if (now < cooldownUntilRef.current) return;
      // While the overlay is on, ignore clicks (they're on our backdrop)
      if (activeRef.current) return;
      // Rolling ring buffer of last N pointerdown times
      const buf = clicksRef.current;
      buf.push(now);
      if (buf.length > CLICKS_REQUIRED) buf.shift();
      if (buf.length === CLICKS_REQUIRED && (now - buf[0]) <= TIME_WINDOW_MS) {
        clicksRef.current = [];
        cooldownUntilRef.current = now + COOLDOWN_MS;
        trigger();
      }
    };
    const trigger = () => {
      setLine(FUNNY_LINES[Math.floor(Math.random() * FUNNY_LINES.length)]);
      setActive(true);
      activeRef.current = true;
      // Screen shake — apply to <html> so the whole page rattles
      document.documentElement.classList.add("cq-screen-shake");
      setTimeout(() => document.documentElement.classList.remove("cq-screen-shake"), 720);
      // Comedic honk-quack via Web Audio (respects any global sound gate too)
      playHonk();
      setTimeout(() => { setActive(false); activeRef.current = false; }, OVERLAY_MS);
    };
    // Capture phase so it runs even if the click hits a stopPropagation target
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => window.removeEventListener("pointerdown", onPointerDown, true);
  }, []);

  if (!active) return null;

  return (
    <div
      className="cq-slowdown-overlay"
      role="alertdialog"
      aria-live="assertive"
      aria-label="Slow down easter egg"
      data-testid="slowdown-easter-egg"
    >
      <div className="cq-slowdown-backdrop" />
      {/* Motion lines / speed streaks radiating from the sign */}
      <div className="cq-slowdown-streaks" aria-hidden>
        {[...Array(12)].map((_, i) => (
          <span key={i} className="cq-slowdown-streak" style={{ transform: `rotate(${i * 30}deg)` }} />
        ))}
      </div>
      <div className="cq-slowdown-stage">
        {/* Giant STOP sign — spins in from above with over-shoot */}
        <div className="cq-slowdown-sign" data-testid="slowdown-stop-sign">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="cq-stop-red" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="55%" stopColor="#dc2626" />
                <stop offset="100%" stopColor="#7f1d1d" />
              </linearGradient>
            </defs>
            {/* Outer octagon shadow */}
            <polygon
              points="61,12 139,12 188,61 188,139 139,188 61,188 12,139 12,61"
              fill="rgba(0,0,0,0.35)" transform="translate(4,6)"
            />
            {/* White border */}
            <polygon
              points="60,10 140,10 190,60 190,140 140,190 60,190 10,140 10,60"
              fill="url(#cq-stop-red)" stroke="#ffffff" strokeWidth="8" strokeLinejoin="round"
            />
            {/* Inner white outline for depth */}
            <polygon
              points="70,22 130,22 178,70 178,130 130,178 70,178 22,130 22,70"
              fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"
            />
            {/* Bolt rivets at 4 corners */}
            {[
              [30, 30], [170, 30], [170, 170], [30, 170]
            ].map(([x, y], i) => (
              <g key={i}>
                <circle cx={x} cy={y} r="4.5" fill="#fca5a5" />
                <circle cx={x - 1} cy={y - 1} r="1.5" fill="#ffffff" opacity="0.85" />
              </g>
            ))}
            <text
              x="100" y="118"
              textAnchor="middle"
              fill="#ffffff"
              style={{ fontFamily: "Impact, 'Arial Black', system-ui, sans-serif", fontWeight: 900, fontSize: 62, letterSpacing: -2 }}
            >
              STOP
            </text>
          </svg>
        </div>

        {/* Duck bouncing in from the right, holding a "SLOW DOWN" pose */}
        <div className="cq-slowdown-duck" data-testid="slowdown-duck">
          <CinematicDuck />
        </div>

        {/* Speech bubble — pops in with cartoon spring */}
        <div className="cq-slowdown-bubble" data-testid="slowdown-message">
          <span>SLOW DOWN, PARTNER!</span>
          <span className="cq-slowdown-bubble-tail" aria-hidden />
        </div>

        {/* Sub-line */}
        <div className="cq-slowdown-tagline">{line}</div>

        {/* Confetti */}
        <div className="cq-slowdown-confetti" aria-hidden>
          {[...Array(28)].map((_, i) => {
            const angle = (i / 28) * Math.PI * 2;
            const dist = 220 + (i % 6) * 30;
            const colors = ["#fbbf24", "#dc2626", "#f97316", "#0ea5e9", "#22c55e", "#a855f7"];
            return (
              <span
                key={i}
                className="cq-slowdown-piece"
                style={{
                  // eslint-disable-next-line
                  "--dx": `${Math.cos(angle) * dist}px`,
                  "--dy": `${Math.sin(angle) * dist}px`,
                  background: colors[i % colors.length],
                  animationDelay: `${(i % 8) * 0.04}s`,
                  transform: `rotate(${(i * 37) % 360}deg)`,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Comedic quack-honk — sawtooth freq sweep through a resonant bandpass. */
function playHonk() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = 0.22;
    master.connect(ctx.destination);

    // First honk
    honkAt(ctx, master, ctx.currentTime, 380, 210, 0.28);
    // Second, higher honk 200ms later
    honkAt(ctx, master, ctx.currentTime + 0.24, 540, 300, 0.24);

    // Auto-close context after the sound ends
    setTimeout(() => { try { ctx.close(); } catch {} }, 900);
  } catch {}
}

function honkAt(ctx, master, when, fStart, fEnd, dur) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const bp = ctx.createBiquadFilter();
  osc.type = "sawtooth";
  bp.type = "bandpass";
  bp.frequency.value = 900;
  bp.Q.value = 3.5;
  osc.frequency.setValueAtTime(fStart, when);
  osc.frequency.exponentialRampToValueAtTime(fEnd, when + dur);
  gain.gain.setValueAtTime(0.0001, when);
  gain.gain.exponentialRampToValueAtTime(0.9, when + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  osc.connect(bp).connect(gain).connect(master);
  osc.start(when);
  osc.stop(when + dur + 0.03);
}
